import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Card,
  Statistic,
  Button,
  Spin,
  Alert,
  notification,
  Modal,
  Dropdown,
} from "antd";
import {
  UserAddOutlined,
  ShareAltOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  GiftOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  MailOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import QRCode from "react-qr-code";
import QRCodeLib from "qrcode";
import { generateInviteLink, getInviteStats } from "../../lib/api/inviteAPI";
import { firebaseAuth } from "../../helpers/authentication";
import { device, useBreakpoints } from "../../lib/breakpoints";

const InviteStatsCard = () => {
  const { isMobile } = useBreakpoints();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);

  // Load invite statistics and invite URL
  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getInviteStats();
        setStats(data);
      } catch (error) {
        // Only log errors that aren't related to missing backend endpoints
        if (
          !error.message.includes("API URL not configured") &&
          !error.message.includes("Failed to fetch") &&
          !error.message.includes("404") &&
          !error.message.includes("Not Found") &&
          !error.message.includes("TypeError: Failed to fetch") &&
          !error.message.includes("Backend API endpoint not available") &&
          !error.message.includes("Auth Error:") &&
          !error.message.includes("Failed to generate invite link") &&
          !error.message.includes("Failed to validate invite code") &&
          !error.message.includes("Failed to use invite code") &&
          !error.message.includes("Failed to get invite statistics") &&
          !error.message.includes("No token provided") &&
          !error.message.includes("Invalid token") &&
          !error.message.includes("User not authenticated") &&
          !error.message.includes("Failed to get authentication token") &&
          !error.message.includes("Unauthorized") &&
          !error.message.includes("Forbidden")
        ) {
          console.error("Error loading invite stats:", error);
        }

        // Don't show error message if backend endpoints aren't implemented yet
        if (
          error.message.includes("API URL not configured") ||
          error.message.includes("Failed to fetch") ||
          error.message.includes("404") ||
          error.message.includes("Not Found") ||
          error.message.includes("TypeError: Failed to fetch") ||
          error.message.includes("Backend API endpoint not available") ||
          error.message.includes("Auth Error:") ||
          error.message.includes("Failed to generate invite link") ||
          error.message.includes("Failed to validate invite code") ||
          error.message.includes("Failed to use invite code") ||
          error.message.includes("Failed to get invite statistics") ||
          error.message.includes("No token provided") ||
          error.message.includes("Invalid token") ||
          error.message.includes("User not authenticated") ||
          error.message.includes("Failed to get authentication token") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("Forbidden")
        ) {
          // Enhanced error logging with specific details
          if (error.message.includes("Auth Error:")) {
            console.log("🔧 Invite System Status:", {
              status:
                "Backend endpoints exist but authentication not configured",
              endpoint: "GET /api/invites/stats",
              response: "Invalid token (401)",
              action: "Backend needs Firebase token validation setup",
              userAction:
                "Contact backend team to implement Firebase auth middleware",
              details: {
                currentError: error.message,
                frontendReady: true,
                backendEndpoints: "exist",
                authMiddleware: "missing",
                nextStep: "Implement Firebase Admin SDK token verification",
              },
            });
          } else if (
            error.message.includes("Backend API endpoint not available")
          ) {
            console.log("🔧 Invite System Status:", {
              status: "Backend endpoints not implemented",
              endpoint: "GET /api/invites/stats",
              response: "HTML error page (404/500)",
              action: "Backend needs invite API endpoints",
              userAction: "Contact backend team to implement invite endpoints",
              details: {
                currentError: error.message,
                frontendReady: true,
                backendEndpoints: "missing",
                authMiddleware: "not applicable",
                nextStep: "Implement invite API endpoints",
              },
            });
          } else {
            console.log("🔧 Invite System Status:", {
              status:
                "Backend endpoints exist but authentication not configured",
              endpoint: "GET /api/invites/stats",
              response: "Invalid token (401)",
              action: "Backend needs Firebase token validation setup",
              userAction:
                "Contact backend team to implement Firebase auth middleware",
              details: {
                currentError: error.message,
                frontendReady: true,
                backendEndpoints: "exist",
                authMiddleware: "missing",
                nextStep: "Implement Firebase Admin SDK token verification",
              },
            });
          }
          // Set default stats for now
          setStats({
            linkClicks: 0,
            signups: 0,
            pendingPayments: 0,
            completedReferrals: 0,
          });
        } else {
          notification.error({
            message: "Failed to load invite statistics",
            description: "Please try again later",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    const loadInviteUrl = () => {
      // Generate UID-based invite link (no backend call needed)
      try {
        const inviteUrl = generateInviteLink();
        setInviteUrl(inviteUrl);
      } catch (error) {
        console.error("Failed to generate invite link:", error);
        setInviteUrl(null);
      }
    };

    loadStats();
    loadInviteUrl();
  }, []);

  // Copy invite URL to clipboard

  // Copy invite URL to clipboard
  const handleCopyInvite = async () => {
    if (inviteUrl) {
      try {
        await navigator.clipboard.writeText(inviteUrl);
        notification.success({
          message: "Invite link copied!",
          description: "The invite link has been copied to your clipboard",
        });
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        notification.error({
          message: "Failed to copy invite link",
          description: "Please try copying the link manually",
        });
      }
    }
  };

  // Generate share message (concise for social media)
  const getShareMessage = () => {
    return `Join me on Clumse! 🏠 AI property management that saves 20+ hours/month. We both get a FREE MONTH when you sign up! 🎁`;
  };

  // Share to social media
  const handleSocialShare = (platform) => {
    if (!inviteUrl) return;

    const message = getShareMessage();
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(inviteUrl);

    let shareUrl = "";

    switch (platform) {
      case "facebook": {
        // Facebook allows longer messages, include URL and message
        const facebookMessage = `${message} ${inviteUrl}`;
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(
          facebookMessage
        )}`;
        break;
      }
      case "twitter": {
        // Twitter has 280 char limit, keep message short
        const twitterMessage = `Join me on Clumse! 🏠 AI property management. We both get a FREE MONTH! 🎁 ${inviteUrl}`;
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          twitterMessage
        )}`;
        break;
      }
      case "linkedin": {
        // LinkedIn allows longer messages, include URL and message
        const linkedinMessage = `${message} ${inviteUrl}`;
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodeURIComponent(
          linkedinMessage
        )}`;
        break;
      }
      case "email": {
        // Email can be longer, include full message
        const emailBody = `${message}\n\n${inviteUrl}`;
        shareUrl = `mailto:?subject=Join me on Clumse&body=${encodeURIComponent(
          emailBody
        )}`;
        break;
      }
      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  // Download QR code
  const handleDownloadQR = async () => {
    if (!inviteUrl) return;

    try {
      // Generate QR code as PNG data URL using qrcode library
      const qrDataUrl = await QRCodeLib.toDataURL(inviteUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      // Create download link
      const link = document.createElement("a");
      link.download = "clumse-invite-qr.png";
      link.href = qrDataUrl;
      link.click();

      notification.success({
        message: "QR Code Downloaded!",
        description: "Your invite QR code has been saved to your device.",
      });
    } catch (error) {
      console.error("Error generating QR code:", error);
      notification.error({
        message: "Download Failed",
        description: "Could not generate QR code. Please try again.",
      });
    }
  };

  // Social media sharing menu items
  const shareMenuItems = [
    {
      key: "facebook",
      label: "Facebook",
      icon: <FacebookOutlined />,
      onClick: () => handleSocialShare("facebook"),
    },
    {
      key: "twitter",
      label: "Twitter",
      icon: <TwitterOutlined />,
      onClick: () => handleSocialShare("twitter"),
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: <LinkedinOutlined />,
      onClick: () => handleSocialShare("linkedin"),
    },
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Spin size="large" />
          <div style={{ marginTop: "1rem" }}>Loading invite statistics...</div>
        </div>
      </Card>
    );
  }

  return (
    <InviteCard>
      {/*
      <CardHeader>
        <HeaderTitle>
          <GiftOutlined style={{ marginRight: '0.5rem', color: 'var(--color-primary)' }} />
          Invite Friends
        </HeaderTitle>
        <HeaderSubtitle>
          Invite other landlords and earn FREE MONTHS when they sign up
        </HeaderSubtitle>
      </CardHeader>
      */}

      <StatsGrid>
        <Statistic
          title="Link Clicks"
          value={stats?.linkClicks || 0}
          prefix={<ShareAltOutlined />}
          valueStyle={{ color: "var(--color-primary)" }}
        />
        <Statistic
          title="Signups"
          value={stats?.signups || 0}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: "var(--color-success)" }}
        />
        <Statistic
          title="Pending Payments"
          value={stats?.pendingPayments || 0}
          prefix={<ClockCircleOutlined />}
          valueStyle={{ color: "var(--color-warning)" }}
        />
        <Statistic
          title="Completed Referrals"
          value={stats?.completedReferrals || 0}
          prefix={<UserAddOutlined />}
          valueStyle={{ color: "var(--color-primary)" }}
        />
      </StatsGrid>

      <InviteSection>
        <InviteTitle>Your Invite Link</InviteTitle>
        <InviteDescription>
          Share your invite link with other landlords. When they sign up and
          complete payment,
          <strong> you both get a FREE MONTH</strong>.
        </InviteDescription>

        <InviteUrlContainer>
          <InviteUrlInput value={inviteUrl || ""} readOnly />
          <ButtonGroup>
            <CopyButton
              onClick={handleCopyInvite}
              disabled={!inviteUrl}
              icon={
                <CopyOutlined
                  style={{ filter: "brightness(0) invert(1) !important" }}
                />
              }
            >
              {isMobile && "Copy Link"}
            </CopyButton>
            <QRButton
              onClick={() => setQrModalVisible(true)}
              disabled={!inviteUrl}
              icon={<QrcodeOutlined style={{ filter: "brightness(0)" }} />}
            >
              {isMobile && "QR Code"}
            </QRButton>
            <Dropdown
              menu={{ items: shareMenuItems }}
              placement="topRight"
              disabled={!inviteUrl}
            >
              <ShareButton icon={<ShareAltOutlined />}>
                {isMobile && "Share"}
              </ShareButton>
            </Dropdown>
          </ButtonGroup>
        </InviteUrlContainer>
      </InviteSection>

      <RewardInfo>
        <Alert
          message={
            isMobile ? "FREE MONTH FOR BOTH!" : "FREE MONTH FOR BOTH OF YOU!"
          }
          description="When someone signs up using your invite link and completes their first payment, you BOTH get a free month of service! That's 2 free months total - one for you, one for them."
          type="info"
          showIcon={false}
        />
      </RewardInfo>

      {/* Backend not ready notice */}
      {stats && stats.totalInvites === 0 && stats.usedInvites === 0 && (
        <BackendNotice>
          <Alert
            message="Invite System Coming Soon"
            description="The invite system frontend is ready! Once your backend implements the invite API endpoints, you'll be able to generate invite links and track referrals."
            type="warning"
            showIcon
          />
        </BackendNotice>
      )}

      {/* QR Code Modal */}
      <Modal
        title="QR Code for Your Invite Link"
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={
              <DownloadOutlined style={{ filter: "brightness(0) invert(1)" }} />
            }
            onClick={handleDownloadQR}
          >
            Download
          </Button>,
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            Close
          </Button>,
        ]}
        centered
        width={400}
      >
        <QRCodeContainer>
          <QRCodeWrapper>
            <QRCode
              id="qr-code"
              value={inviteUrl || ""}
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            />
          </QRCodeWrapper>
          <QRCodeDescription>
            Scan this QR code to quickly access your invite link and share it
            with other landlords!
          </QRCodeDescription>
        </QRCodeContainer>
      </Modal>
    </InviteCard>
  );
};

const InviteCard = styled(Card)`
  margin-bottom: 2rem;
  background: var(--gradient-primary);
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
  overflow: hidden;
  position: relative;

  .ant-card-body {
    padding: 2.5rem;
    background: transparent;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      padding: 1.5rem 1rem;
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.04) 100%
    );
    pointer-events: none;
    z-index: 0;
  }

  /* Add a subtle shimmer effect */
  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
    pointer-events: none;
    z-index: 0;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%) translateY(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) translateY(100%) rotate(45deg);
    }
  }
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const HeaderTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 2.25rem;
  font-weight: 700;
  color: white;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.875rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.625rem;
    margin-bottom: 0.625rem;
  }

  .anticon {
    font-size: 2rem;
    margin-right: 0.875rem;
    filter: none;
    color: #ffd700;

    @media (max-width: 768px) {
      font-size: 1.75rem;
      margin-right: 0.75rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
      margin-right: 0.625rem;
    }
  }
`;

const HeaderSubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 500;
  line-height: 1.5;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .ant-statistic {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 1.5rem 1rem;
    border: 2px solid rgba(255, 255, 255, 0.25);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 1rem 0.75rem;
      border-radius: 12px;
    }

    @media (max-width: 480px) {
      padding: 1.25rem 1rem;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 100%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.25);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
      border-color: rgba(255, 255, 255, 0.35);
    }
  }

  /* Brand-colored accents per stat (greens) */
  .ant-statistic:nth-child(1),
  .ant-statistic:nth-child(2),
  .ant-statistic:nth-child(3),
  .ant-statistic:nth-child(4) {
    box-shadow:
      inset 0 0 0 2px rgba(16, 185, 129, 0.35),
      0 10px 24px rgba(16, 185, 129, 0.18);
  }

  .ant-statistic-title {
    font-family: var(--font-secondary);
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0.5rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      font-size: 0.75rem;
      margin-bottom: 0.25rem;
    }
  }

  .ant-statistic-content {
    font-family: var(--font-primary);
    font-size: 2rem;
    font-weight: 800;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }

    @media (max-width: 480px) {
      font-size: 1.75rem;
    }
  }

  .ant-statistic-content-value {
    color: white;
  }

  .anticon {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.4rem;
    filter: none;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
`;

const InviteSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const InviteTitle = styled.h4`
  font-family: var(--font-primary);
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0 0 1.25rem 0;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1.375rem;
    margin-bottom: 0.875rem;
  }
`;

const InviteDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-bottom: 1.25rem;
  }

  strong {
    color: #ffd700;
    font-weight: 700;
    font-size: 1.1rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: pulse-glow 2s ease-in-out infinite alternate;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }

  @keyframes pulse-glow {
    0% {
      text-shadow:
        0 2px 4px rgba(0, 0, 0, 0.3),
        0 0 10px rgba(255, 215, 0, 0.3);
    }
    100% {
      text-shadow:
        0 2px 4px rgba(0, 0, 0, 0.3),
        0 0 20px rgba(255, 215, 0, 0.6);
    }
  }
`;

const InviteUrlContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 1.25rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1rem;
    border-radius: 16px;
    gap: 1rem;
    flex-direction: column;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 12px;
    gap: 0.75rem;
    flex-direction: column;
    align-items: stretch;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0.1) 25%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 75%,
      rgba(255, 255, 255, 0.3) 100%
    );
    border-radius: 22px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow:
      0 16px 48px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.3);

    &::after {
      opacity: 1;
    }
  }
`;

const InviteUrlInput = styled.input`
  flex: 1;
  padding: 0.875rem 1.125rem;
  border: none;
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  background: rgba(255, 255, 255, 0.08);
  color: black;
  font-weight: 500;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(15px);
  transition: all 0.3s ease;
  letter-spacing: 0.025em;
  background: rgba(255, 255, 255, 0.12);
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.3),
    inset 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    border-radius: 10px;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.8rem;
    border-radius: 8px;
    width: 100%;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.12);
    box-shadow:
      0 0 0 2px rgba(255, 255, 255, 0.3),
      inset 0 1px 3px rgba(0, 0, 0, 0.1);
    transform: scale(1.01);
  }

  &:hover:not(:focus) {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CopyButton = styled(Button)`
  background: none;
  border: none;
  color: white;
  font-family: var(--font-secondary);
  font-weight: 600;
  border-radius: 14px;
  min-width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);

  /* Hide text on desktop, show only on mobile */
  .ant-btn-icon {
    margin-right: 0;
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    height: 48px;
    border-radius: 12px;
    flex-shrink: 0;
    font-size: 0.9rem;
    gap: 0.75rem;

    /* Show text on mobile */
    span:not(.anticon) {
      display: inline;
    }
  }

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
    font-size: 0.85rem;
    gap: 0.625rem;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.35);
    color: white;
    transform: translateY(-2px) scale(1.02);
    box-shadow:
      0 8px 25px rgba(0, 0, 0, 0.12),
      0 4px 8px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(1.01);
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.08),
      0 2px 4px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  .anticon {
    font-size: 1.1rem;
    color: #000;
    filter: none;
    transition: all 0.2s ease;

    @media (max-width: 768px) {
      font-size: 1rem;
      color: #000;
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
      color: #000;
    }
  }

  /* Ensure button text is white */
  span:not(.anticon) {
    color: white;
  }

  &:hover:not(:disabled) .anticon {
    transform: scale(1.05);
  }
`;

const GenerateButton = styled(Button)`
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
  font-family: var(--font-secondary);
  font-weight: 600;
  height: 48px;
  padding: 0 2rem;

  &:hover:not(:disabled) {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
    width: 100%;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.625rem;
    align-items: stretch;
    width: 100%;
  }
`;

const QRButton = styled(Button)`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  font-family: var(--font-secondary);
  font-weight: 600;
  border-radius: 12px;
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(15px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  /* Hide text on desktop, show only on mobile */
  .ant-btn-icon {
    margin-right: 0;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
    transform: scale(1.08) translateY(-1px);
    box-shadow:
      0 6px 20px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(1.02) translateY(0px);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    height: 48px;
    border-radius: 12px;
    flex-shrink: 0;
    font-size: 0.9rem;
    gap: 0.75rem;

    /* Show text on mobile */
    span:not(.anticon) {
      display: inline;
    }
  }

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
    font-size: 0.85rem;
    gap: 0.625rem;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  .anticon {
    font-size: 1.1rem;
    color: #000;
    filter: none;
    transition: all 0.2s ease;

    @media (max-width: 768px) {
      font-size: 1rem;
      color: #000;
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
      color: #000;
    }
  }

  /* Ensure button text is white */
  span:not(.anticon) {
    color: white;
  }

  &:hover:not(:disabled) .anticon {
    transform: scale(1.1);
  }
`;

const ShareButton = styled(Button)`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  font-family: var(--font-secondary);
  font-weight: 600;
  border-radius: 12px;
  min-width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(15px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  /* Hide text on desktop, show only on mobile */
  .ant-btn-icon {
    margin-right: 0;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
    transform: scale(1.08) translateY(-1px);
    box-shadow:
      0 6px 20px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(1.02) translateY(0px);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    height: 48px;
    border-radius: 12px;
    flex-shrink: 0;
    font-size: 0.9rem;
    gap: 0.75rem;

    /* Show text on mobile */
    span:not(.anticon) {
      display: inline;
    }
  }

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
    font-size: 0.85rem;
    gap: 0.625rem;
  }

  .anticon {
    font-size: 1.1rem;
    color: #000;
    filter: none;
    transition: all 0.2s ease;

    @media (max-width: 768px) {
      font-size: 1rem;
      color: #000;
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
      color: #000;
    }
  }

  /* Ensure button text is white */
  span:not(.anticon) {
    color: white;
  }

  &:hover:not(:disabled) .anticon {
    transform: scale(1.1);
  }
`;

const QRCodeContainer = styled.div`
  text-align: center;
  padding: 1rem 0;
`;

const QRCodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
`;

const QRCodeDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const RewardInfo = styled.div`
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }

  .ant-alert {
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(20px);
    border: 3px solid transparent;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.12),
        rgba(255, 255, 255, 0.12)
      ),
      var(--gradient-primary);
    background-origin: border-box;
    background-clip: padding-box, border-box;
    border-radius: 18px;
    box-shadow:
      0 12px 40px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.04),
      0 0 25px rgba(255, 215, 0, 0.25);
    position: relative;
    overflow: hidden;
    animation: reward-pulse 3s ease-in-out infinite;

    @media (max-width: 768px) {
      border-radius: 12px;
      padding: 1rem;
    }

    @media (max-width: 480px) {
      border-radius: 10px;
      padding: 0.875rem;
    }

    .ant-alert-message {
      color: #ffd700 !important;
      font-weight: 700 !important;
      font-size: 1.2rem !important;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      font-family: var(--font-primary) !important;
      letter-spacing: -0.01em;

      @media (max-width: 768px) {
        font-size: 1.1rem !important;
      }

      @media (max-width: 480px) {
        font-size: 1rem !important;
      }
    }

    .ant-alert-description {
      color: rgba(255, 255, 255, 0.95) !important;
      font-weight: 500 !important;
      font-size: 1.05rem !important;
      line-height: 1.5;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

      @media (max-width: 768px) {
        font-size: 0.95rem !important;
      }

      @media (max-width: 480px) {
        font-size: 0.9rem !important;
      }
    }

    .ant-alert-icon {
      filter: brightness(0) invert(1);
    }
  }

  @keyframes reward-pulse {
    0%,
    100% {
      box-shadow:
        0 12px 40px rgba(0, 0, 0, 0.08),
        0 4px 12px rgba(0, 0, 0, 0.04),
        0 0 25px rgba(255, 215, 0, 0.25);
      transform: scale(1);
    }
    50% {
      box-shadow:
        0 16px 50px rgba(0, 0, 0, 0.12),
        0 6px 16px rgba(0, 0, 0, 0.06),
        0 0 35px rgba(255, 215, 0, 0.4);
      transform: scale(1.01);
    }
  }
`;

const BackendNotice = styled.div`
  margin-top: 1.5rem;

  .ant-alert {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;

    .ant-alert-message {
      color: white;
      font-weight: 600;
    }

    .ant-alert-description {
      color: rgba(255, 255, 255, 0.85);
    }

    .anticon {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`;

export default InviteStatsCard;
